import { useMemo } from 'react';
import { useResponsiveLayout } from '../../../hooks/ui/useResponsiveLayout';
import {
  overlayFooterRowStyle,
  overlayFooterButtonStyle,
  overlayFooterLinkStyle,
  overlayFooterStackStyle,
} from '../../../ui/overlayFooter';

/**
 * The adoption hook for the ONE overlay footer (KAN-948). Returns ready-made styles whose size
 * follows touch (`isTouch`: coarse pointer OR under 1024) - 38px on web, 44px under a finger.
 *
 *   const f = useOverlayFooter();                       // padded-panel modal
 *   const f = useOverlayFooter({ sectioned: true });    // panel with padding: 0 (wizard, notice, export)
 *   const f = useOverlayFooter({ bare: true });         // inside DrawerShell's `footer` (it owns the inset)
 *   const f = useOverlayFooter({ bare: true, equal: true }); // + Cancel / Save split 50/50 (drawer save footers)
 *
 *   <div style={f.row}>
 *     <button type="button" style={f.cancel} onClick={requestClose}>Cancel</button>
 *     <button type="button" style={f.primary} onClick={save}>Save</button>      // or f.danger
 *   </div>
 *
 * A third choice goes in `f.stack` ABOVE the row:  <div style={f.stack}><button style={f.link}>…</button><div style={f.row}>…</div></div>
 * (the row inside the stack uses `f.rowInStack`, no top margin).
 *
 * Disabled variants: `f.primaryDisabled`, `f.dangerDisabled`, `f.cancelDisabled`.
 * Hand over to `ActionBeatFill` with `confirmStyle={f.primary}` / `cancelStyle={f.cancel}` /
 * `rowStyle={f.row}` - the box is then identical before and after the beat.
 */
export default function useOverlayFooter({ sectioned = false, bare = false, equal = false } = {}) {
  const { isTouch } = useResponsiveLayout();
  return useMemo(() => {
    const row = overlayFooterRowStyle({ isTouch, sectioned, bare });
    return {
      isTouch,
      row,
      rowInStack: { ...row, marginTop: 0 },
      stack: overlayFooterStackStyle(),
      cancel: overlayFooterButtonStyle({ isTouch, kind: 'cancel', equal }),
      primary: overlayFooterButtonStyle({ isTouch, kind: 'primary', equal }),
      danger: overlayFooterButtonStyle({ isTouch, kind: 'danger', equal }),
      cancelDisabled: overlayFooterButtonStyle({ isTouch, kind: 'cancel', disabled: true, equal }),
      primaryDisabled: overlayFooterButtonStyle({ isTouch, kind: 'primary', disabled: true, equal }),
      dangerDisabled: overlayFooterButtonStyle({ isTouch, kind: 'danger', disabled: true, equal }),
      link: overlayFooterLinkStyle({ isTouch }),
    };
  }, [isTouch, sectioned, bare, equal]);
}
